/// <amd-module name="Wit/Common/PlanningPane/Services/PlanningPaneViewService" />
import { adjustIterationDatesFromServer } from "Agile/Common/Utils/Date";
import { IIterationData } from "Agile/Contracts/Common";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { routeUrl } from "VSS/Platform/Location";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IterationDetailsData } from "Wit/BacklogsHub/Common/Contracts/BacklogsHub.Models";
import { IChangeIterationWorkItemData, ICurrentIterationData } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import * as Resources from "Wit/Common/PlanningPane/Resources";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";

export const FUTURE_ITERATION_CAP_KEY = "Agile/BacklogsHub/PlanningIterationFutureCount";
const DEFAULT_FUTURE_ITERATION_CAP = 10;

namespace BacklogsHubRoutes {
    export const BacklogsHubContent = "ms.vss-work-web.new-backlogs-content-route";
    export const SprintsContent = "ms.vss-work-web.new-sprints-content-route";
}

namespace PlanningPaneDataProviders {
    export const IterationDetailsDataProvider = "ms.vss-work-web.iteration-details-data-provider";
}

export interface IPlanningPaneViewService extends IVssService {
    planningItemProvider: ArrayItemProvider<IIterationData | string>;
    getBacklogUrl(teamName: string): string;
    getIterationDetails(teamId: string, iteration: IIterationData, ignoreCache?: boolean): ObservableValue<IterationDetailsData | undefined>;
    getIterationUrl(teamName: string, iteration: IIterationData): string;
    getIterations(): IIterationData[];
    updateIterationPanel(iterationIndex: number, workItems: IChangeIterationWorkItemData[], numberOfSavedWorkitems: number): void;
    updateIterations(): void;
    getIterationInfo(iterationIndex: number): ICurrentIterationData;
}

class PlanningPaneViewService extends VssService implements IPlanningPaneViewService {
    private iterationDetailsCache: { [iterationId: string]: ObservableValue<IterationDetailsData | undefined> } = {};
    private teamSettingsSvc: ITeamSettingsService;
    public planningItemProvider: ArrayItemProvider<IIterationData | string>;

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.teamSettingsSvc = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
    };

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
    };

    public getBacklogUrl(teamName: string): string {
        const routeValues: { [key: string]: string } = this.getDefaultRouteValues();
        routeValues["pivot"] = "backlog";
        routeValues["teamName"] = teamName;
        return routeUrl(this.pageContext, BacklogsHubRoutes.BacklogsHubContent, routeValues);
    }

    public getIterationDetails(teamId: string, iteration: IIterationData, ignoreCache?: boolean): ObservableValue<IterationDetailsData | undefined> {
        this.ensureIterationDetails(teamId, iteration, ignoreCache);
        return this.iterationDetailsCache[iteration.id];
    }

    public getIterationUrl(teamName: string, iteration: IIterationData): string {
        const routeValues: { [key: string]: string } = this.getDefaultRouteValues();
        const urlFriendlyPath = iteration.friendlyPath.replace(/\\/g, "/");
        routeValues["pivot"] = "backlog";
        routeValues["teamName"] = teamName;
        routeValues["iteration"] = urlFriendlyPath;
        return routeUrl(this.pageContext, BacklogsHubRoutes.SprintsContent, routeValues);
    }

    public getIterations(): IIterationData[] {
        const teamSettings = this.teamSettingsSvc.getTeamSettingsObservable().value;
        const items: IIterationData[] = [];

        if (!teamSettings) {
            return items;
        }
        if (teamSettings.futureIterations && teamSettings.futureIterations.length > 0) {
            const adjustedIterations = teamSettings.futureIterations.map(iteration => adjustIterationDatesFromServer(iteration));
            const filteredIterations = this.applyCapToIterations(adjustedIterations);
            items.push(...filteredIterations);
        }
        if (teamSettings.currentIteration) {
            items.unshift(adjustIterationDatesFromServer(teamSettings.currentIteration));
        }
        return items;
    }

    public getIterationInfo(iterationIndex: number): ICurrentIterationData {
        const iteration = this.planningItemProvider.value[iterationIndex];
        const teamSettings = this.teamSettingsSvc.getTeamSettingsObservable().value;

        let path: string | undefined;
        let newIterationPath: string | undefined = undefined;
        let newNodeId: number | undefined;
        let iterationId: string | undefined;
        let iterationName: string | undefined;

        if (typeof iteration === "string") {
            // Backlog level (string) uses "friendlyPath" for path wheras in else statement, actual iteration levels use "path"
            newIterationPath = teamSettings?.backlogIteration.friendlyPath;
            newNodeId = teamSettings?.backlogIteration.nodeId;
            path = newIterationPath;
            iterationId = teamSettings?.backlogIteration.id;
            iterationName = teamSettings?.backlogIteration.displayText;
        } else {
            newIterationPath = iteration.friendlyPath;
            newNodeId = iteration.nodeId;
            path = iteration.path;
            iterationId = iteration.id;
            iterationName = iteration.name;
        }

        const result: ICurrentIterationData = {
            path,
            newIterationPath,
            iterationId,
            newNodeId,
            iterationName
        };

        return result;
    }

    public updateIterationPanel(iterationIndex: number, workItems: IChangeIterationWorkItemData[], numberOfSavedWorkitems: number): void {
        const iteration = this.planningItemProvider.value[iterationIndex];
        const teamSettings = this.teamSettingsSvc.getTeamSettingsObservable().value;
        const currentIterationData = this.getIterationInfo(iterationIndex);

        if (currentIterationData.path && currentIterationData.newIterationPath) {
            const teamId = teamSettings?.teamId;

            // Get previous iterationPath
            const prevIterationPaths: { prevIterationPath: string; workItemType: string }[] = workItems.map(workItem => {
                return {
                    prevIterationPath: workItem.workItemIterationPath,
                    workItemType: workItem.workItemTypeName
                };
            });

            const cachedOldIterations: IterationDetailsData[] = this.loadingAndCacheOldIterations(
                workItems,
                currentIterationData.newIterationPath,
                this.planningItemProvider,
                currentIterationData.iterationId
            );

            cachedOldIterations.map(cachedOldIteration => {
                if (cachedOldIteration) {
                    this.iterationDetailsCache[cachedOldIteration.iterationId].value = cachedOldIteration;
                }
            });

            this.updateIterationWorkItemCountsLocally(
                prevIterationPaths,
                currentIterationData.newIterationPath,
                teamId,
                iteration,
                this.planningItemProvider
            );

            if (numberOfSavedWorkitems > 0) {
                const messageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                messageService.addToast({
                    duration: 2000,
                    message: format(Resources.MoveIterationsToastMessage, numberOfSavedWorkitems, currentIterationData.iterationName)
                });
            }
        }
    }

    public updateIterations() {
        this.planningItemProvider.value.forEach(iteration => {
            if (typeof iteration === "string") {
                return;
            }
            this.getIterationDetails(this.teamSettingsSvc.getTeamSettingsObservable().value?.teamId ?? "", iteration, /** ignoreCache */ true);
        });
    }

    private applyCapToIterations(iterations: IIterationData[]): IIterationData[] {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const futureIterationCap = settingsService.getEntry<number>(FUTURE_ITERATION_CAP_KEY, SettingsUserScope.Host) || DEFAULT_FUTURE_ITERATION_CAP;
        return iterations.slice(0, futureIterationCap);
    }

    private getDefaultRouteValues(): { [key: string]: string } {
        const routeValues: { [key: string]: string } = {};
        const projectName = getTFSPageData(this.pageContext)?.project?.name;
        routeValues["project"] = projectName || "";

        return routeValues;
    }

    private async ensureIterationDetails(
        teamId: string,
        iteration: IIterationData,
        ignoreCache?: boolean
    ): Promise<IterationDetailsData | undefined> {
        if (this.iterationDetailsCache[iteration.id] === undefined) {
            this.iterationDetailsCache[iteration.id] = new ObservableValue<IterationDetailsData | undefined>(undefined);
        }

        if (this.iterationDetailsCache[iteration.id].value === undefined || ignoreCache) {
            const parameters = {
                teamId,
                iterationId: iteration.id
            };

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.iterationDetailsCache[iteration.id].value = await contributionService.getDataAsync<IterationDetailsData>(
                PlanningPaneDataProviders.IterationDetailsDataProvider,
                parameters,
                true
            );
        }

        return this.iterationDetailsCache[iteration.id].value;
    }

    private loadingAndCacheOldIterations(
        workItems: IChangeIterationWorkItemData[],
        newIterationPath: string | undefined,
        planningItemProvider: ArrayItemProvider<string | IIterationData>,
        iterationId: string | undefined
    ): IterationDetailsData[] {
        const cachedOldIterations: IterationDetailsData[] = [];

        // Loading spinner for previous iterations, except backlog level which has no counts
        workItems.map(workItem => {
            const oldNodeId = workItem.workItemIterationId;
            const oldNodePath = workItem.workItemIterationPath;

            if (oldNodePath !== newIterationPath) {
                const oldIteration = planningItemProvider.value.find((iteration: IIterationData) => iteration.nodeId === oldNodeId) as IIterationData;

                if (oldIteration) {
                    const oldIterationId = oldIteration?.id;

                    const oldIterationDetails = this.iterationDetailsCache[oldIterationId]?.value;
                    if (oldIterationDetails) {
                        cachedOldIterations.push(oldIterationDetails);
                    }
                    if (this.iterationDetailsCache[oldIterationId]) {
                        this.iterationDetailsCache[oldIterationId].value = undefined;
                    }
                }
            }

            // Loading spinner for future iteration, except backlog level which has no counts
            if (oldNodePath !== newIterationPath && iterationId && this.iterationDetailsCache[iterationId]) {
                this.iterationDetailsCache[iterationId].value = undefined;
            }
        });

        return cachedOldIterations;
    }

    private updateIterationWorkItemCountsLocally(
        prevIterationItems: { prevIterationPath: string; workItemType: string }[],
        newIterationPath: string | undefined,
        teamId: string | undefined,
        iteration: string | IIterationData,
        planningItemProvider: ArrayItemProvider<string | IIterationData>
    ) {
        prevIterationItems.forEach(prevIterationItem => {
            // If prev and new iterationPath are the same, do nothing, work item isn't moving anywhere.
            if (prevIterationItem.prevIterationPath !== newIterationPath) {
                // Get work item's previous iteration
                const prevWorkItemIteration = planningItemProvider.value.find(
                    (i: IIterationData) => i.friendlyPath === prevIterationItem.prevIterationPath
                );
                if (prevWorkItemIteration !== undefined) {
                    const workItemIterationDetails = this.getIterationDetails(teamId || "", prevWorkItemIteration as IIterationData);
                    const prevIteration = workItemIterationDetails.value;

                    // Subtract count from previous iteration
                    if (prevIteration?.workItemTypeToCountMap[prevIterationItem.workItemType]) {
                        prevIteration.workItemTypeToCountMap[prevIterationItem.workItemType] =
                            prevIteration.workItemTypeToCountMap[prevIterationItem.workItemType] - 1;

                        // If setting to 0, see if everything else is 0 or not.  If everything is 0, reset to empty {} to trigger removal of work item type icon
                        if (prevIteration?.workItemTypeToCountMap[prevIterationItem.workItemType] === 0) {
                            delete prevIteration?.workItemTypeToCountMap[prevIterationItem.workItemType];
                        }

                        workItemIterationDetails.value = prevIteration;
                    }
                }

                // Get new iterationPath changes, this will update the observable value with the new count and is undefined until this call
                this.getIterationDetails(teamId || "", iteration as IIterationData);
            }
        });
    }
}

Services.add("IPlanningPaneViewService", { serviceFactory: PlanningPaneViewService });
